import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, PencilSimple, Trash, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";

export default function SalesReturns() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/sales-returns"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const del = async (id) => {
    if (!confirm("Delete this credit note? Returned stock will be reversed.")) return;
    try { await api.delete(`/sales-returns/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Sales</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Sales Returns / Credit Notes</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Returns credit the customer ledger and add stock back into inventory.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PageExportBar
            title="Sales Returns"
            filename={`sales-returns-${new Date().toISOString().slice(0,10)}`}
            columns={[
              { key: "number", label: "Credit Note #" },
              { key: "date", label: "Date" },
              { key: "customer_name", label: "Customer" },
              { key: "grand_total", label: "Amount" },
              { key: "reason", label: "Reason" },
            ]}
            rows={rows}
            testidPrefix="returns"
          />
          <Link to="/returns/new">
            <Button className="h-9" data-testid="new-return-btn">
              <Plus size={15} weight="bold" className="mr-1.5" /> New Credit Note
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Credit Note #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Reason</th>
              <th>Original Invoice</th>
              <th className="num">Amount</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">
                No credit notes yet. Create one to record a return.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`return-row-${r.id}`}>
                <td className="font-mono text-[12.5px]">
                  <Link to={`/returns/${r.id}`} className="text-primary hover:underline">{r.number}</Link>
                </td>
                <td>{fmtDate(r.date)}</td>
                <td className="font-medium">{r.customer_name}</td>
                <td className="text-muted-foreground text-[12.5px]">{r.reason || "—"}</td>
                <td className="font-mono text-[12px] text-muted-foreground">{r.original_invoice_number || "—"}</td>
                <td className="num font-semibold">
                  <span className="inline-flex items-center gap-1 text-destructive">
                    <ArrowUUpLeft size={12} weight="bold" />
                    {inr(r.grand_total)}
                  </span>
                </td>
                <td className="num" style={{ textAlign: "right" }}>
                  <Link to={`/returns/${r.id}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8"><PencilSimple size={14} /></Button>
                  </Link>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`return-del-${r.id}`}>
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
